import React, { useEffect } from 'react'
import { trackCustomPageView, trackButtonClick } from '../../services/analytics';

interface ModalProps {
  isOpen: boolean
  title?: string
  onClose?: () => void
  clearBtn?: string
  submitBtn?: string
  onPrimary?: () => void
  onSecondary?: () => void
  primaryDisabled?: boolean
  secondaryDisabled?: boolean
  children?: React.ReactNode
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  title = '',
  onClose,
  clearBtn,
  submitBtn,
  onPrimary,
  onSecondary,
  primaryDisabled = false,
  secondaryDisabled = false,
  children,
}) => {
  useEffect(() => {
    // Emit open event when the modal is mounted so App's overlay shows
    if (isOpen && typeof globalThis !== 'undefined') {
      globalThis.dispatchEvent(new CustomEvent('app-modal-open'))
      
      const modalName = title || 'Modal';
      console.log(`📊 Analytics: Modal opened - ${modalName}`);
      trackCustomPageView('manager-portal:add-notes');
    }
    return () => {
      if (typeof globalThis !== 'undefined') {
        globalThis.dispatchEvent(new CustomEvent('app-modal-close'))
      }
    }
  }, [isOpen, title])

  if (!isOpen) return null

  const closeAndEmit = () => {
    if (typeof globalThis !== 'undefined') {
      globalThis.dispatchEvent(new CustomEvent('app-modal-close'))
    }
    if (onClose) onClose()
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeAndEmit()
    }
  }

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      closeAndEmit()
    }
  }

  const modal = (
    <div aria-hidden="false" className="modal-pattern" id="modal-manual-trig" data-ds-theme="vessel" data-ds-variant="windowed" data-analytics-location="modal-example-modal-two" style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2147483648 }}>
      <div
        className="modal-fade-screen"
        onClick={handleBackdropClick}
        onKeyDown={handleBackdropKeyDown}
        role="presentation"
        tabIndex={-1}
        style={{ position: 'absolute', inset: 0 }}
      ></div>
      <div className="modal-container rows" style={{ position: 'relative', zIndex: 2147483649, width: '75rem', top: '82px' }}>
        <div className="modal-inner modal-icons-enabled columns-8 w-[100%]" role="dialog" aria-labelledby="modal-header-alert">

          <div className="modal-header">
            {title ? <h2 className="modal-title" id="modal-header-alert">{title}</h2> : <h2 className="modal-title" id="modal-header-alert">Modal</h2>}

            <button aria-label="Close" data-dismiss="x-close" data-analytics-type="button" onClick={closeAndEmit} data-analytics-click="Close [X] Modal">
              <img alt="close icon" src="/assets/images/close-outline.svg" width="30" height="30" />
            </button>
          </div>
          <div className="modal-content">
            <div className="modal-content-body" data-content-path="/content/kporg/en/national/content-fragments/documents/paperless/13.html">

              <div className="modal-content-inner">
                {children}
              </div>
            </div>
            <div className="modal-actions">
              <div className="modal-buttons -right">

                <button
                  className={`button -inverted  modal-btn2 ${secondaryDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-analytics-click="secondary button"
                  data-analytics-type="button"
                  data-success="btn2"
                  onClick={() => { 
                    if (!secondaryDisabled) { 
                      // Track button click
                      const pageKey = globalThis.window.location.pathname.replaceAll('/', ':') || 'unknown';
                      console.log(`📊 Analytics: Modal secondary button clicked - ${clearBtn || 'Cancel'}`);
                      trackButtonClick(clearBtn || 'Cancel', 'modal-secondary', { pageKey });
                      
                      onSecondary?.();
                    } 
                  }}
                  disabled={secondaryDisabled}
                >
                  {clearBtn}
                </button>

                <button
                  className={`button -primary -light modal-btn1 modal__cancel-btn ${primaryDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-analytics-click="Primary Button"
                  data-analytics-type="button"
                  data-success="btn1"
                  onClick={() => { 
                    if (!primaryDisabled) { 
                      // Track button click
                      const pageKey = globalThis.window.location.pathname.replaceAll('/', ':') || 'unknown';
                      console.log(`📊 Analytics: Modal primary button clicked - ${submitBtn || 'Submit'}`);
                      trackButtonClick(submitBtn || 'Submit', 'modal-primary', { pageKey });
                      
                      onPrimary?.(); 
                      closeAndEmit();
                    } 
                  }}
                  disabled={primaryDisabled}
                >
                  {submitBtn}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div>
      {modal}
    </div>
  )
}

export default Modal